import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";

function InvoicePicker({ invoices, selected, onSelect }) {
  return (
    <div className="max-h-56 overflow-y-auto border border-border rounded-sm divide-y divide-border" data-testid="reverse-invoice-list">
      {invoices.map((inv) => (
        <button key={inv.id} onClick={() => onSelect(inv)} data-testid={`reverse-invoice-${inv.doc_no}`}
          className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${selected?.id === inv.id ? "bg-primary/10" : "hover:bg-secondary/60"}`}>
          <span className="flex flex-col">
            <span className="font-mono text-xs font-semibold">{inv.doc_no}</span>
            <span className="text-[11px] text-muted-foreground">{inv.date}{inv.external_doc_no ? ` · ${inv.external_doc_no}` : ""} · {inv.status}</span>
          </span>
          <span className="text-right text-xs tabular">
            <span className="block font-semibold">{money(inv.total)}</span>
            <span className="block text-muted-foreground">Paid {money(inv.amount_paid)}{inv.amount_credited ? ` · Credited ${money(inv.amount_credited)}` : ""}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function LinePicker({ lines, picks, setPicks }) {
  const toggle = (l, on) => setPicks((p) => ({ ...p, [l.line_id]: on ? l.remaining : undefined }));
  return (
    <table className="w-full text-sm" data-testid="reverse-lines-table">
      <thead>
        <tr className="text-left border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="py-1.5 w-8" /><th className="py-1.5">Description</th><th className="py-1.5 text-right">Unit Price</th>
          <th className="py-1.5 text-right">Left to reverse</th><th className="py-1.5 text-right w-28">Qty to reverse</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => {
          const on = picks[l.line_id] !== undefined;
          return (
            <tr key={l.line_id} className="border-b border-border/60" data-testid="reverse-line-row">
              <td className="py-1.5"><Checkbox checked={on} onCheckedChange={(v) => toggle(l, !!v)} data-testid="reverse-line-check" /></td>
              <td className="py-1.5">{l.description}</td>
              <td className="py-1.5 text-right tabular">{money(l.unit_price)}</td>
              <td className="py-1.5 text-right tabular">{l.remaining}</td>
              <td className="py-1.5 text-right">
                <Input type="number" min={0} max={l.remaining} step="any" disabled={!on} value={on ? picks[l.line_id] : ""}
                  onChange={(e) => setPicks((p) => ({ ...p, [l.line_id]: e.target.value }))} className="h-8 rounded-sm text-right text-xs" data-testid="reverse-line-qty" />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ReversePostedLinesDialog({ open, onOpenChange, cmId, onDone }) {
  const [inv, setInv] = useState(null);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["reversible-invoices", cmId],
    queryFn: async () => (await api.get(`/sales-orders/${cmId}/reversible-invoices`)).data,
    enabled: open && !!cmId,
    staleTime: 0,
  });
  useEffect(() => { if (!open) { setInv(null); setPicks({}); } }, [open]);
  const pick = (i) => { setInv(i); setPicks(Object.fromEntries(i.lines.map((l) => [l.line_id, l.remaining]))); };
  const chosen = Object.entries(picks).filter(([, q]) => q !== undefined && Number(q) > 0);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/sales-orders/${cmId}/get-posted-lines`, { invoice_id: inv.id, lines: chosen.map(([line_id, q]) => ({ line_id, quantity: Number(q) })) });
      toast.success(`Lines from ${inv.doc_no} added`);
      onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="reverse-lines-dialog">
        <DialogHeader>
          <DialogTitle>Get Posted Document Lines to Reverse</DialogTitle>
          <DialogDescription>Pick a posted invoice for this customer, then choose the lines and quantities to credit.</DialogDescription>
        </DialogHeader>
        {isLoading ? <p className="text-sm text-muted-foreground py-6 text-center">Loading invoices…</p>
          : invoices.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="reverse-no-invoices">No posted invoices with lines left to reverse for this customer.</p>
          : <InvoicePicker invoices={invoices} selected={inv} onSelect={pick} />}
        {inv && <div className="mt-2"><p className="text-xs font-semibold mb-1">Lines on {inv.doc_no}</p><LinePicker lines={inv.lines} picks={picks} setPicks={setPicks} /></div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={!inv || chosen.length === 0 || busy} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="reverse-lines-confirm">
            {busy ? "Adding…" : `Get ${chosen.length} line${chosen.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
